import { ArrowLeft, Loader2, LockOpen } from "lucide-react";

import { Button } from "@/components/ui/button";
import { formatVnd } from "@/components/boi-de/topic-style";
import { IconVanKhi } from "@/components/nav-icons";
import { cn } from "@/lib/utils";
import type { VanKhiKind, VanKhiPeriod } from "@/lib/boide-api";

import { VanKhiCountdown } from "./van-khi-countdown";
import { KIND_META } from "./van-khi-style";

const BADGE: Record<VanKhiPeriod["state"], { text: string; className: string }> = {
  open: { text: "Miễn phí", className: "border-element-wood/35 text-element-wood" },
  closed: { text: "Kỳ đã qua", className: "border-border text-muted-foreground" },
  locked: { text: "Kỳ sắp tới", className: "border-border text-muted-foreground" },
};

/**
 * Màn cổng khi kỳ chưa có bài luận.
 *
 * - Kỳ hiện tại (`open`): mở miễn phí.
 * - Kỳ đã qua / sắp tới: mở bằng số dư (giá do BE quyết). Kỳ sắp tới còn hiện đồng
 *   hồ tới lúc nó thành kỳ hiện tại — chờ tới hôm đó thì xem miễn phí.
 *
 * Trạng thái do server quyết định — component chỉ hiển thị.
 */
export function VanKhiGate({
  kind,
  period,
  serverNow,
  nextOpenAt,
  opening,
  onOpen,
  onPrevious,
  canGoPrevious,
  canPurchase,
  price,
}: {
  kind: VanKhiKind;
  period: VanKhiPeriod;
  serverNow: number;
  nextOpenAt: string | null;
  opening: boolean;
  onOpen: () => void;
  onPrevious: () => void;
  canGoPrevious: boolean;
  canPurchase: boolean;
  price: number;
}) {
  const meta = KIND_META[kind];
  const badge = BADGE[period.state];
  const isOpen = period.state === "open";
  // Kỳ sắp tới: đếm tới lúc nó thành kỳ hiện tại (khi ấy xem miễn phí).
  const countdownTarget = period.state === "locked" ? period.open_at : null;
  const title = isOpen
    ? `${meta.noun} đã sẵn sàng`
    : period.state === "locked"
      ? `Xem trước ${meta.noun.toLowerCase()}`
      : `Xem lại ${meta.noun.toLowerCase()}`;
  const description = isOpen
    ? "Thầy đã soạn sẵn bài luận chi tiết: khí của kỳ, công việc, tài lộc, tình cảm, sức khoẻ, giờ tốt và hướng xuất hành."
    : period.state === "locked"
      ? "Kỳ này chưa tới. Mở ngay bằng số dư để chuẩn bị trước, hoặc chờ tới kỳ để xem miễn phí."
      : "Kỳ này đã qua. Mở bằng số dư để xem lại vận khí của kỳ.";

  return (
    <section className="relative overflow-hidden almanac-panel px-5 py-8 text-center sm:px-8 sm:py-10">
      <span
        aria-hidden="true"
        className="absolute -right-16 -top-16 size-44 rounded-full border border-gold-soft/25"
      />
      <div className="relative flex flex-col items-center gap-4">
        <span className="grid size-14 place-items-center rounded-full border border-gold-soft/60 text-primary">
          <IconVanKhi className="size-8" />
        </span>
        <span
          className={cn("rounded-full border px-3 py-1 text-[10px] font-semibold", badge.className)}
        >
          {badge.text}
        </span>

        <div className="max-w-md space-y-1.5">
          <h2 className="font-display text-xl font-bold text-foreground sm:text-2xl">{title}</h2>
          <p className="text-sm text-muted-foreground">
            {period.label}
            {period.sub_label ? ` · ${period.sub_label}` : ""}
          </p>
          <p className="pt-1 text-xs leading-5 text-muted-foreground">{description}</p>
        </div>

        {isOpen ? (
          <div className="flex flex-col items-center gap-3">
            <Button size="lg" onClick={onOpen} disabled={opening} className="min-w-[180px]">
              {opening ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  Thầy đang luận…
                </>
              ) : (
                <>
                  <IconVanKhi className="mr-2 size-4" />
                  Xem lời khuyên
                </>
              )}
            </Button>
            {period.close_at ? (
              <VanKhiCountdown
                targetIso={period.close_at}
                serverNow={serverNow}
                label="Còn miễn phí trong"
              />
            ) : null}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-5">
            {countdownTarget ? (
              <VanKhiCountdown
                targetIso={countdownTarget}
                serverNow={serverNow}
                label="Miễn phí khi tới kỳ, còn"
              />
            ) : null}

            {/* Kỳ đã qua / sắp tới mở bằng số dư — giá do BE quyết. */}
            {canPurchase ? (
              <div className="flex flex-col items-center gap-2">
                <Button size="lg" onClick={onOpen} disabled={opening} className="min-w-[200px]">
                  {opening ? (
                    <>
                      <Loader2 className="mr-2 size-4 animate-spin" />
                      Thầy đang luận…
                    </>
                  ) : (
                    <>
                      <LockOpen className="mr-2 size-4" />
                      Mở xem — {formatVnd(price)}
                    </>
                  )}
                </Button>
                <span className="text-[11px] text-muted-foreground">1 VND = 1 token</span>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Kỳ này nằm ngoài khoảng có thể xem.</p>
            )}
            {canGoPrevious ? (
              <div className="flex flex-col items-center gap-2">
                <Button variant="secondary" onClick={onPrevious}>
                  <ArrowLeft className="mr-2 size-4" />
                  Xem kỳ trước
                </Button>
              </div>
            ) : null}
          </div>
        )}

        <p className="max-w-sm border-t border-border/60 pt-4 text-[11px] leading-5 text-muted-foreground">
          {meta.window}
        </p>
      </div>
    </section>
  );
}
